import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { defaultMenuPermissions } from "@/constants/adminMenu";
import api from "@/lib/api";
import {
    LayoutDashboard, Users, Wrench, CalendarDays, FileText, FolderOpen, Database, Map, LogOut, Bell, Sparkles, Truck, Package, Clock, UserPlus, ClipboardCheck, HardDrive
} from "lucide-react";

export default function AdminShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const handleLogout = () => { logout(); nav("/login"); };
    const [alertCount, setAlertCount] = useState(0);
    const isOperator = user?.role === "operator";
    const isAuxiliar = user?.role === "auxiliar";

    const refreshAlertCount = async () => {
        try {
            const r = await api.get("/admin/alerts/summary");
            setAlertCount(r.data?.open_total || 0);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        if (isOperator || isAuxiliar) return;
        refreshAlertCount();
        const t = setInterval(refreshAlertCount, 60000);
        return () => clearInterval(t);
    }, [isOperator, isAuxiliar]);

    const allLinks = [
        { to: "/admin", key: "dashboard", icon: LayoutDashboard, label: "Dashboard", end: true, admin: true },
        { to: "/admin/clients", key: "clients", icon: Users, label: "Clientes", auxiliar: true },
        { to: "/admin/technicians", key: "technicians", icon: Wrench, label: "Técnicos" },
        { to: "/admin/schedules", key: "schedules", icon: CalendarDays, label: "Programación", admin: true },
        { to: "/admin/reports", key: "reports", icon: FileText, label: "Reportes", admin: true },
        { to: "/admin/documents", key: "documents", icon: FolderOpen, label: "Documentos", auxiliar: true },
        { to: "/admin/floor-plans", key: "floor-plans", icon: Map, label: "Mapas", auxiliar: true },
        { to: "/admin/alerts", key: "alerts", icon: Bell, label: "Alertas", badge: alertCount, admin: true },
        { to: "/admin/executive-reports", key: "executive-reports", icon: Sparkles, label: "Informes Ejec.", admin: true },
        { to: "/admin/transportes", key: "transportes", icon: Truck, label: "Transportes", auxiliar: true },
        { to: "/admin/auditores", key: "auditores", icon: ClipboardCheck, label: "Auditores", admin: true },
        { to: "/admin/insumos", key: "insumos", icon: Package, label: "Insumos", admin: true },
        { to: "/admin/equipos", key: "equipos", icon: Wrench, label: "Equipos", admin: true },
        { to: "/admin/asistencia", key: "asistencia", icon: Clock, label: "Asistencia", admin: true },
        { to: "/admin/catalogo", key: "catalogo", icon: Database, label: "Catálogo Docs", auxiliar: true },
        { to: "/admin/odoo", key: "odoo", icon: Database, label: "Odoo (Mock)", admin: true },
        { to: "/admin/usuarios", key: "usuarios", icon: UserPlus, label: "Usuarios", admin: true },
        { to: "/admin/almacenamiento", key: "almacenamiento", icon: HardDrive, label: "Almacenamiento", admin: true },
    ];

    // Admin => full access. Others: filter by their saved menu_permissions (fallback to role defaults).
    const perms = Array.isArray(user?.menu_permissions)
        ? user.menu_permissions
        : defaultMenuPermissions(user?.role);
    const links = user?.role === "admin"
        ? allLinks
        : allLinks.filter(l => perms.includes(l.key));

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <aside className="hidden lg:flex w-64 flex-col bg-[hsl(var(--trulyn-navy))] text-white">
                <div className="px-4 py-4 border-b border-white/10 flex items-center justify-center">
                    <BrandLogo size={56} light withSubtitle={false} />
                </div>
                <nav className="flex-1 px-2 py-3 space-y-0.5">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                                        : "text-white/85 hover:bg-white/10"
                                }`
                            }
                            data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                        >
                            <l.icon className="w-4 h-4" />
                            <span className="flex-1">{l.label}</span>
                            {l.badge > 0 && (
                                <span
                                    className="bg-[#FF0000] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center"
                                    data-testid={`admin-nav-${l.label.toLowerCase()}-badge`}
                                >
                                    {l.badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10">
                    <div className="text-xs text-white/60 mb-2">{user?.email}</div>
                    <Button variant="outline" size="sm" onClick={handleLogout} className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" data-testid="admin-logout-button">
                        <LogOut className="w-4 h-4 mr-1" /> Salir
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="bg-white border-b">
                    <div className="px-4 lg:px-6 py-3 flex items-center justify-between">
                        <div className="lg:hidden"><BrandLogo size={28} /></div>
                        <div className="hidden lg:flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{isOperator ? "Panel Operador" : "Panel Administrativo"}</span>
                            {!isOperator && (
                                <span className="pill pill-completed" data-testid="admin-odoo-sync-indicator">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-600" />
                                    Odoo: Conectado (Mock)
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="hidden sm:inline text-sm"><span className="text-muted-foreground">Hola, </span><span className="font-medium">{user?.full_name?.split(" ")[0]}</span></span>
                            <Button variant="ghost" size="icon" onClick={handleLogout} className="lg:hidden" title="Salir">
                                <LogOut className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                    {/* Mobile nav */}
                    <nav className="lg:hidden flex overflow-x-auto border-t">
                        {links.map((l) => (
                            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) =>
                                `px-3 py-2 text-xs whitespace-nowrap flex items-center gap-1 ${isActive ? "text-[hsl(var(--trulyn-navy))] font-semibold border-b-2 border-[hsl(var(--accent))]" : "text-muted-foreground"}`
                            }>
                                <l.icon className="w-3.5 h-3.5" />
                                <span>{l.label}</span>
                                {l.badge > 0 && (
                                    <span className="bg-[#FF0000] text-white text-[9px] font-bold rounded-full px-1 ml-0.5">
                                        {l.badge}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                </header>
                <main className="flex-1 p-4 lg:p-6 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
